import { ParsedUrlQuery } from "node:querystring"
import { MutableRefObject, useEffect } from "react"
import * as F from "fp-ts/function"
import { NextRouter } from "next/router"
import { deepEqual } from "fast-equals"
import * as P from "fp-ts/Predicate"
import * as O from "fp-ts/Option"
import { useActor } from "@xstate/react"
import { NormalQueryParam, replaceQuery, updateQuery } from "../../../_common/routing"
import { usePageTransition } from "../../../_common/hooks/use-page-transition"
import { Command, diffUrlQueryParams } from "../../../_common/diff-url-query-params"
import { notPresentParamsFromContext, paramsFromQuery, presentParamsFromContext } from "../query-params"
import { ECommerceService } from "../machines/ecommerce-machine/e-commerce-machine"
import { useWorkQueueMachine } from "../../../_common/machines/machines-context"
import { decodeSuite } from "../../../_common/suite-encoding"
import { usePrevious } from "./use-previous"

export const useSyncUrlOnStateChange = (
    isRestoreComplete: MutableRefObject<boolean>,
    router: NextRouter,
    ecommerce: ECommerceService,
): void => {
    const [state] = useActor(ecommerce)
    const [stateWQ] = useWorkQueueMachine()
    const { schedule } = usePageTransition()

    const currentQuery = paramsFromQuery(router.query)
    const presentParams = presentParamsFromContext(state.context)
    const notPresentParams = notPresentParamsFromContext(state.context)
    const paramsToSet = filterEmptyParams({ ...currentQuery, ...presentParams }, notPresentParams)
    const previous = usePrevious(paramsToSet)

    const sameParams = deepEqual(currentQuery, paramsToSet)
    const sameAsPrevious = deepEqual(previous, paramsToSet)
    const paramsDidNotChange = sameParams || sameAsPrevious

    const diff = diffUrlQueryParams(previous, paramsToSet)

    const isAutomaticSuiteSelection = state.context.isAutomaticSelection

    const requireReplace = F.pipe(
        // NOTE: suite number is peculiar because is selected by default, and we don't want to have
        // url in history that represent a state without a suite number selected
        isOfType("suite-number", "add"),
        P.or(isOfType("guests", "update")),
        P.or(isOfType("gateway", "update")),
        P.or(isOfType("airfare", "update")),
        P.or(isOfType("fare", "update")),
        P.or(P.and(isOfType("fare", "add"))(() => isAutomaticSuiteSelection)),
        P.or(P.and(isOfType("suite", "add"))(() => isAutomaticSuiteSelection)),
        P.or(P.and(isOfType("suite", "update"))(isNotQuoteCategory)),
        P.or(isOfType("suite-number", "update")),
    )
    const shouldUseReplace = diff.every(requireReplace)
    const isWorkInProgress = !stateWQ.matches("empty")

    useEffect(() => {
        if (!isRestoreComplete.current) return
        if (isWorkInProgress) return
        if (paramsDidNotChange) return

        // console.log(`syncUrl - ${shouldUseReplace ? "replace" : "update"}`)
        if (shouldUseReplace) schedule({ key: "replaceQuery", action: () => replaceQuery(router, paramsToSet) })
        else schedule({ key: "updateQuery", action: () => updateQuery(router, paramsToSet) })
    }, [
        isRestoreComplete,
        isWorkInProgress,
        paramsDidNotChange,
        router,
        JSON.stringify(paramsToSet),
        schedule,
        shouldUseReplace,
    ])
}

const filterEmptyParams = (query: ParsedUrlQuery, paramsToCleanup: string[]): ParsedUrlQuery => {
    return Object.fromEntries(Object.entries(query).filter((p) => !paramsToCleanup.includes(p[0])))
}

const isOfType =
    (paramName: NormalQueryParam, diffType: Command["type"]) =>
    (x: Command): boolean =>
        x.key === paramName && x.type === diffType

const isNotQuoteCategory = (x: Command): boolean => {
    if (x.key !== "suite") return true

    const categorySource = F.pipe(
        decodeSuite(x.value),
        O.map((x) => x.source),
        O.toUndefined,
    )
    return categorySource !== "quote"
}
